import {
    DATA_TYPE_OPTIONS,
    FILE_PROVIDER_TYPES,
    FULLY_EDITABLE_PROVIDER_TYPES,
    SQL_PROVIDER_TYPES,
    URL_ONLY_EDITABLE_PROVIDER_TYPES,
} from './constants.ts';
import { DataConnectionType } from './types.ts';
import type { ConnectionTypeModel, DataTypeValue, SelectedSourceColumn, SourceColumn, SourceConfig, SourceObject } from './types.ts';

const providerFallbackNames: Record<number, string> = {
    [DataConnectionType.Csv]: 'CSV',
    [DataConnectionType.Excel]: 'Microsoft Excel',
    [DataConnectionType.MsAccess]: 'Microsoft Access',
    [DataConnectionType.MsSqlServer]: 'Microsoft SQL Server',
    [DataConnectionType.PostgreSql]: 'PostgreSQL',
};

const CSV_ENCODING = 'utf8';

export function isFileProvider(type?: DataConnectionType | null): boolean {
    return type != null && FILE_PROVIDER_TYPES.has(type);
}

export function isSqlProvider(type?: DataConnectionType | null): boolean {
    return type != null && SQL_PROVIDER_TYPES.has(type);
}

export function getDefaultConnectionConfig(type?: DataConnectionType | null): Record<string, unknown> {
    switch (type) {
        case DataConnectionType.Csv:
            return { filepath: '', hasheader: true, delimiter: ',', encoding: CSV_ENCODING };
        case DataConnectionType.Excel:
        case DataConnectionType.MsAccess:
            return { filepath: '' };
        case DataConnectionType.MsSqlServer:
            return {
                serverhost: '',
                port: '',
                database: '',
                authentication: 'sql',
                username: '',
                password: '',
                encryptconnection: false,
                trustservercertificate: true,
            };
        case DataConnectionType.PostgreSql:
            return { serverhost: '', port: 5432, database: '', username: '', password: '' };
        default:
            return {};
    }
}

export function mergeConnectionConfigWithDefaults(type: DataConnectionType | undefined, config?: Record<string, unknown> | null): Record<string, unknown> {
    const merged = {
        ...getDefaultConnectionConfig(type),
        ...(config || {}),
    };

    if (type === DataConnectionType.Csv) {
        merged.encoding = CSV_ENCODING;
    }

    return merged;
}

export function normalizeDataType(value: string | null | undefined): DataTypeValue {
    const normalized = (value || '').toLowerCase();
    if (normalized === 'string') {
        return 'text';
    }

    return DATA_TYPE_OPTIONS.some(option => option.value === normalized)
        ? normalized as DataTypeValue
        : 'text';
}

export function getSourceDataType(column: SourceColumn | SelectedSourceColumn): DataTypeValue {
    const sourceValue = 'datatype' in column ? column.sourcedatatype : column.SourceDataType;
    const dataTypeValue = 'datatype' in column ? column.datatype : column.DataType;
    const normalizedSource = sourceValue ? normalizeDataType(sourceValue) : normalizeDataType(dataTypeValue);
    return normalizedSource === 'url' ? 'text' : normalizedSource;
}

export function isFullyEditableProvider(type?: DataConnectionType | null): boolean {
    return type != null && FULLY_EDITABLE_PROVIDER_TYPES.has(type);
}

export function isUrlOnlyEditableProvider(type?: DataConnectionType | null): boolean {
    return type != null && URL_ONLY_EDITABLE_PROVIDER_TYPES.has(type);
}

export function isDataTypeAllowedForProvider(type: DataConnectionType | undefined, sourceDataType: DataTypeValue, dataType: DataTypeValue): boolean {
    if (dataType === 'url') {
        return sourceDataType === 'text';
    }

    if (isFullyEditableProvider(type)) {
        return true;
    }

    if (isUrlOnlyEditableProvider(type)) {
        return sourceDataType === 'text' && dataType === 'text';
    }

    return dataType === sourceDataType;
}

export function coerceDataTypeForProvider(type: DataConnectionType | undefined, sourceDataType: DataTypeValue, dataType: DataTypeValue): DataTypeValue {
    return isDataTypeAllowedForProvider(type, sourceDataType, dataType) ? dataType : sourceDataType;
}

export function getProviderDisplayName(connectionTypes: ConnectionTypeModel[], type?: DataConnectionType | null, fallback?: string): string {
    if (type == null) {
        return fallback || '';
    }

    const match = connectionTypes.find(item => item.Type === type);
    return match?.DisplayName || match?.Name || fallback || providerFallbackNames[type] || String(type);
}

export function getSourceObjectDisplay(source?: SourceObject | SourceConfig | null): string {
    if (!source) {
        return '';
    }

    const schemaName = 'Name' in source ? source.SchemaName : source.schemaname;
    const name = 'Name' in source ? source.Name : source.name;
    return schemaName ? `${schemaName}.${name}` : name;
}

export function getSourceObjectKey(source: SourceObject | SourceConfig): string {
    return 'Identifier' in source
        ? source.Identifier || getSourceObjectDisplay(source)
        : source.identifier || getSourceObjectDisplay(source);
}

export function getColumnKey(column: SourceColumn | SelectedSourceColumn): string {
    return 'Identifier' in column
        ? column.Identifier || column.Name
        : column.identifier || column.name;
}

export function toSourceObject(config: SourceConfig): SourceObject {
    return {
        Type: config.type,
        SchemaName: config.schemaname || null,
        Name: config.name,
        Identifier: config.identifier,
    };
}

export function columnsFromSourceConfig(config?: SourceConfig | null): SourceColumn[] {
    return (config?.columns || []).map(column => ({
        Name: column.name,
        Identifier: column.identifier,
        DataType: normalizeDataType(column.datatype),
        SourceDataType: getSourceDataType(column),
    }));
}

export function buildSourceConfig(
    source: SourceObject,
    columns: SourceColumn[],
    selectedColumnKeys: string[],
    dataTypesByColumnKey: Record<string, DataTypeValue>,
): SourceConfig {
    const selectedKeySet = new Set(selectedColumnKeys);
    const selectedColumns = columns
        .filter(column => selectedKeySet.has(getColumnKey(column)))
        .map<SelectedSourceColumn>(column => {
            const key = getColumnKey(column);
            return {
                name: column.Name,
                identifier: column.Identifier,
                datatype: normalizeDataType(dataTypesByColumnKey[key] || column.DataType),
                sourcedatatype: getSourceDataType(column),
            };
        });

    return {
        type: (source.Type || '').toLowerCase(),
        ...(source.SchemaName ? { schemaname: source.SchemaName } : {}),
        name: source.Name,
        identifier: source.Identifier,
        columns: selectedColumns,
    };
}

export function hasConnectionConfigChanged(original: Record<string, unknown> | undefined, current: Record<string, unknown>): boolean {
    return JSON.stringify(original || {}) !== JSON.stringify(current || {});
}
